import axios, { AxiosError } from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { ElMessage } from 'element-plus';
import type { IssuedTokens, ApiErrorPayload } from '@/types';

const TOKEN_KEY = 'sifusheng-admin-brand:tokens';

export interface TokenStore {
  get(): IssuedTokens | null;
  set(tokens: IssuedTokens): void;
  clear(): void;
}

export const tokenStore: TokenStore = {
  get(): IssuedTokens | null {
    try {
      const raw = window.localStorage.getItem(TOKEN_KEY);
      return raw ? (JSON.parse(raw) as IssuedTokens) : null;
    } catch {
      return null;
    }
  },
  set(tokens: IssuedTokens): void {
    window.localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  },
  clear(): void {
    window.localStorage.removeItem(TOKEN_KEY);
  },
};

interface RefreshHook {
  refresh: (refreshToken: string) => Promise<IssuedTokens>;
  onUnauthorized: () => void;
}

let refreshHook: RefreshHook | null = null;

export function bindRefreshHook(hook: RefreshHook): void {
  refreshHook = hook;
}

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

export const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const tokens = tokenStore.get();
  if (tokens?.accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  return config;
});

let pendingRefresh: Promise<IssuedTokens | null> | null = null;

async function tryRefresh(): Promise<IssuedTokens | null> {
  if (pendingRefresh) return pendingRefresh;
  const tokens = tokenStore.get();
  if (!tokens?.refreshToken || !refreshHook) return null;
  pendingRefresh = (async () => {
    try {
      const next = await refreshHook!.refresh(tokens.refreshToken);
      tokenStore.set(next);
      return next;
    } catch {
      tokenStore.clear();
      return null;
    } finally {
      pendingRefresh = null;
    }
  })();
  return pendingRefresh;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  __retry?: boolean;
}

http.interceptors.response.use(
  (resp: AxiosResponse) => resp,
  async (error: AxiosError<ApiErrorPayload>) => {
    const status = error.response?.status;
    const config = error.config as RetriableConfig | undefined;
    if (status === 401 && config && !config.__retry) {
      const refreshed = await tryRefresh();
      if (refreshed && config.headers) {
        config.__retry = true;
        config.headers.Authorization = `Bearer ${refreshed.accessToken}`;
        return http.request(config);
      }
      refreshHook?.onUnauthorized();
      return Promise.reject(error);
    }
    const payload = error.response?.data;
    const raw = payload?.message ?? error.message ?? '请求失败';
    const message = Array.isArray(raw) ? raw.join('; ') : String(raw);
    if (status && status >= 500) {
      ElMessage.error(`服务器错误: ${message}`);
    } else if (status && status !== 401) {
      ElMessage.error(message);
    }
    return Promise.reject(error);
  },
);

export type QueryValue = string | number | boolean | undefined | null;
export type QueryParams = Record<string, QueryValue> | object;

export async function getJson<T>(url: string, params?: QueryParams): Promise<T> {
  const config: AxiosRequestConfig = { params };
  const resp = await http.get<T>(url, config);
  return resp.data;
}

export async function postJson<T, B extends object = object>(url: string, body?: B): Promise<T> {
  const resp = await http.post<T>(url, body);
  return resp.data;
}

export async function patchJson<T, B extends object = object>(url: string, body?: B): Promise<T> {
  const resp = await http.patch<T>(url, body);
  return resp.data;
}

export async function deleteJson<T>(url: string): Promise<T> {
  const resp = await http.delete<T>(url);
  return resp.data;
}
